import { forwardRef, useEffect, useState } from 'react';

import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import {
    Avatar,
    Chip,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Typography,
    useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';

import { Link } from '@redwoodjs/router';

import { SageSystemUserModal } from 'src/components';
import useConfig from 'src/hooks/useConfig';
import { useDispatch, useSelector } from 'src/store';
import { activeItem, openDrawer } from 'src/store/slices/menu';
import { LinkTarget, NavItemType } from 'src/types';

interface NavItemProps {
    item: NavItemType;
    level: number;
}

// ==============================|| SIDEBAR MENU LIST ITEMS ||============================== //

const NavItem = ({ item, level }: NavItemProps) => {
    const theme = useTheme();
    const matchesSM = useMediaQuery(theme.breakpoints.down('lg'));

    const { borderRadius } = useConfig();
    const dispatch = useDispatch();
    const { openItem } = useSelector((state) => state.menu);

    const [isActive, setIsActive] = useState(false);
    const [isSageSystemUserModalOpen, setIsSageSystemUserModalOpen] = useState(false);

    // Define itemIcon here, within the component's scope
    const Icon = item.icon;
    const itemIcon = Icon ? (
        <Icon stroke={1.5} size="1.3rem" />
    ) : (
        <FiberManualRecordIcon
            sx={{
                width: isActive ? 8 : 6,
                height: isActive ? 8 : 6,
            }}
            fontSize={level > 0 ? 'inherit' : 'medium'}
        />
    );

    const pathname = document.location.pathname.toString();

    // This function checks if the item's URL matches the current pathname exactly
    const checkActive = (itemUrl) => pathname === itemUrl;

    useEffect(() => {
        setIsActive(checkActive(item.url));
    }, [pathname, item.url]);

    let listItemProps: {
        component: typeof Link | 'a';
        to?: string;
        href?: string;
        target?: LinkTarget;
    };

    if (item.type !== 'modal') {
        let itemTarget: LinkTarget = '_self';
        if (item.target) {
            itemTarget = '_blank';
        }

        listItemProps = {
            component: forwardRef((props, ref) => (
                <Link ref={ref} {...props} to={item.url} target={itemTarget} />
            )),
        };

        if (item?.external) {
            listItemProps = { component: 'a', href: item.url, target: itemTarget };
        }
    }

    const itemHandler = (id: string, type: string) => {
        if (id === 'sage-system-user' && type === 'modal') {
            setIsSageSystemUserModalOpen(true);
        } else {
            dispatch(activeItem([id]));
            matchesSM && dispatch(openDrawer(false));
        }
    };

    return (
        <>
            <ListItemButton
                {...listItemProps}
                disabled={item.disabled}
                sx={{
                    borderRadius: `${borderRadius}px`,
                    mb: 0.5,
                    alignItems: 'flex-start',
                    backgroundColor:
                        level > 1
                            ? 'transparent !important'
                            : isActive
                            ? theme.palette.action.selected
                            : 'inherit',
                    py: level > 1 ? 1 : 1.25,
                    pl: `${level * 24}px`,
                }}
                selected={isActive}
                onClick={() => itemHandler(item?.id, item.type)}
                data-testid={`${item?.id}-nav-item`}
            >
                <ListItemIcon sx={{ my: 'auto', minWidth: !item?.icon ? 18 : 36 }}>
                    {itemIcon}
                </ListItemIcon>
                <ListItemText
                    primary={
                        <Typography
                            variant={isActive ? 'h5' : 'body1'}
                            color={
                                isActive ? theme.palette.secondary.main : theme.palette.grey[200]
                            }
                        >
                            {item.title}
                        </Typography>
                    }
                    secondary={
                        item.caption && (
                            <Typography
                                variant="caption"
                                sx={{ ...theme.typography.subMenuCaption }}
                                display="block"
                                gutterBottom
                            >
                                {item.caption}
                            </Typography>
                        )
                    }
                />
                {item.chip && (
                    <Chip
                        color={item.chip.color}
                        variant={item.chip.variant}
                        size={item.chip.size}
                        label={item.chip.label}
                        avatar={item.chip.avatar && <Avatar>{item.chip.avatar}</Avatar>}
                    />
                )}
            </ListItemButton>

            {isSageSystemUserModalOpen && (
                <SageSystemUserModal
                    isOpen={isSageSystemUserModalOpen}
                    onClose={() => setIsSageSystemUserModalOpen(false)}
                />
            )}
        </>
    );
};

export default NavItem;
